import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Image, Plus, Edit, Trash2, Eye, EyeOff, GripVertical, Upload, Link as LinkIcon } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api/v1/admin/example-projects`;

const TYPES = [
  { value: 'Image', label: 'Image' },
  { value: 'Video', label: 'Video' },
  { value: 'Ad', label: 'Ad' },
  { value: 'Audio', label: 'Audio' },
];

const TARGET_APPS = [
  { value: 'all', label: 'All Apps' },
  { value: 'creatorx', label: 'CreatorX' },
  { value: 'adstudio', label: 'Ad Studio' },
  { value: 'ad_creator', label: 'Ad Creator' },
  { value: 'advanced_studio', label: 'Advanced Studio' },
  { value: 'movie_maker', label: 'Movie Maker' },
];

const emptyForm = {
  title: '', description: '', type: 'Image',
  sort_order: '', is_active: true, target_app: 'all',
  media_url: '', thumbnail_url: '', project_data: '',
};

export default function ExampleProjects() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [mediaFile, setMediaFile] = useState(null);
  const [thumbFile, setThumbFile] = useState(null);
  const [saving, setSaving] = useState(false);

  const token = localStorage.getItem('admin_token');
  const headers = { Authorization: `Bearer ${token}` };

  const fetchItems = useCallback(async () => {
    try {
      const res = await axios.get(`${API}?include_inactive=true`, { headers });
      setItems(res.data.projects || []);
    } catch (err) {
      toast.error('Failed to fetch projects');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  const openCreate = () => { setEditing(null); setForm(emptyForm); setMediaFile(null); setThumbFile(null); setShowDialog(true); };

  const openEdit = (item) => {
    setEditing(item);
    setForm({
      title: item.title || '', description: item.description || '',
      type: item.type || 'Image', sort_order: item.sort_order ?? '',
      is_active: item.is_active !== false, target_app: item.target_app || 'all',
      media_url: item.media_url || '', thumbnail_url: item.thumbnail_url || '',
      project_data: item.project_data ? JSON.stringify(item.project_data, null, 2) : '',
    });
    setMediaFile(null); setThumbFile(null); setShowDialog(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append('title', form.title);
      fd.append('description', form.description);
      fd.append('type', form.type);
      if (form.sort_order !== '') fd.append('sort_order', form.sort_order);
      fd.append('is_active', form.is_active);
      fd.append('target_app', form.target_app);
      if (form.media_url) fd.append('media_url', form.media_url);
      if (form.thumbnail_url) fd.append('thumbnail_url', form.thumbnail_url);
      if (form.project_data) fd.append('project_data', form.project_data);
      if (mediaFile) fd.append('media_file', mediaFile);
      if (thumbFile) fd.append('thumbnail_file', thumbFile);

      if (editing) {
        await axios.put(`${API}/${editing.id}`, fd, { headers });
        toast.success('Project updated');
      } else {
        await axios.post(API, fd, { headers });
        toast.success('Project created');
      }
      setShowDialog(false); fetchItems();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    } finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this project?')) return;
    try {
      await axios.delete(`${API}/${id}`, { headers });
      toast.success('Project deleted');
      fetchItems();
    } catch (err) { toast.error('Failed to delete'); }
  };

  const toggleActive = async (item) => {
    try {
      const fd = new FormData();
      fd.append('is_active', !item.is_active);
      await axios.put(`${API}/${item.id}`, fd, { headers });
      toast.success(item.is_active ? 'Project hidden' : 'Project visible');
      fetchItems();
    } catch (err) { toast.error('Failed to toggle'); }
  };

  const moveItem = async (index, direction) => {
    const sorted = [...items];
    const swapIdx = index + direction;
    if (swapIdx < 0 || swapIdx >= sorted.length) return;
    const order = sorted.map((item, i) => {
      if (i === index) return { id: item.id, sort_order: sorted[swapIdx].sort_order };
      if (i === swapIdx) return { id: item.id, sort_order: sorted[index].sort_order };
      return { id: item.id, sort_order: item.sort_order };
    });
    try {
      await axios.put(`${API}/reorder`, { order }, { headers: { ...headers, 'Content-Type': 'application/json' } });
      fetchItems();
    } catch (err) { toast.error('Failed to reorder'); }
  };

  const getTypeColor = (type) => {
    const m = { Image: 'bg-purple-100 text-purple-700', Video: 'bg-pink-100 text-pink-700', Ad: 'bg-amber-100 text-amber-700', Audio: 'bg-emerald-100 text-emerald-700' };
    return m[type] || 'bg-slate-100 text-slate-700';
  };

  if (loading) return <div className="flex items-center justify-center h-64 text-slate-500">Loading example projects...</div>;

  return (
    <div>
      <div className="mb-8 flex justify-between items-center">
        <div>
          <h1 data-testid="projects-page-title" className="text-4xl font-bold text-slate-800 mb-2">Example Projects</h1>
          <p className="text-slate-600">Manage example projects shown on user dashboards</p>
        </div>
        <Button data-testid="add-project-btn" onClick={openCreate} className="bg-blue-600 hover:bg-blue-700">
          <Plus className="w-4 h-4 mr-2" /> Add Project
        </Button>
      </div>

      {items.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-lg shadow-md">
          <Image className="w-12 h-12 mx-auto text-slate-400 mb-4" />
          <p className="text-slate-500 mb-4">No example projects yet</p>
          <Button onClick={openCreate} className="bg-blue-600 hover:bg-blue-700"><Plus className="w-4 h-4 mr-2" /> Add First Project</Button>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          <table className="w-full" data-testid="projects-table">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase w-12">#</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Title</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase w-24">Type</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase w-28">Target App</th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-slate-500 uppercase w-20">Active</th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-slate-500 uppercase w-40">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {items.map((item, idx) => (
                <tr key={item.id} className={`hover:bg-slate-50 transition-colors ${!item.is_active ? 'opacity-50' : ''}`} data-testid={`project-row-${item.id}`}>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1">
                      <button onClick={() => moveItem(idx, -1)} disabled={idx === 0} className="text-slate-400 hover:text-slate-600 disabled:opacity-30" data-testid={`project-move-up-${item.id}`}><GripVertical className="w-4 h-4" /></button>
                      <span className="text-sm text-slate-500">{item.sort_order}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="font-medium text-slate-800">{item.title}</div>
                    {item.description && <div className="text-xs text-slate-500 mt-0.5 line-clamp-1">{item.description}</div>}
                    {(item.media_url || item.media_file_path) && <div className="text-xs text-blue-500 mt-0.5 flex items-center gap-1">{item.media_file_path ? <Upload className="w-3 h-3" /> : <LinkIcon className="w-3 h-3" />}{item.media_file_path ? 'Uploaded' : 'URL'}</div>}
                  </td>
                  <td className="px-4 py-3"><span className={`px-2 py-1 rounded-full text-xs font-medium ${getTypeColor(item.type)}`}>{item.type}</span></td>
                  <td className="px-4 py-3"><span className="px-2 py-1 rounded-full text-xs font-medium bg-slate-100 text-slate-700">{item.target_app}</span></td>
                  <td className="px-4 py-3 text-center">
                    <button onClick={() => toggleActive(item)} data-testid={`project-toggle-${item.id}`} className="inline-flex">
                      {item.is_active ? <Eye className="w-4 h-4 text-green-600" /> : <EyeOff className="w-4 h-4 text-slate-400" />}
                    </button>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex justify-end gap-2">
                      <button onClick={() => openEdit(item)} data-testid={`project-edit-${item.id}`} className="p-1.5 rounded-md bg-blue-50 text-blue-600 hover:bg-blue-100"><Edit className="w-4 h-4" /></button>
                      <button onClick={() => handleDelete(item.id)} data-testid={`project-delete-${item.id}`} className="p-1.5 rounded-md bg-red-50 text-red-600 hover:bg-red-100"><Trash2 className="w-4 h-4" /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="sm:max-w-[640px] max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing ? 'Edit Example Project' : 'Add New Example Project'}</DialogTitle></DialogHeader>
          <form onSubmit={handleSubmit}>
            <div className="space-y-4 py-4">
              <div>
                <Label>Title *</Label>
                <Input data-testid="project-form-title" value={form.title} onChange={e => setForm({...form, title: e.target.value})} required />
              </div>
              <div>
                <Label>Description</Label>
                <Textarea data-testid="project-form-description" value={form.description} onChange={e => setForm({...form, description: e.target.value})} rows={2} />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Type</Label>
                  <Select value={form.type} onValueChange={v => setForm({...form, type: v})}>
                    <SelectTrigger data-testid="project-form-type"><SelectValue /></SelectTrigger>
                    <SelectContent>{TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Target App</Label>
                  <Select value={form.target_app} onValueChange={v => setForm({...form, target_app: v})}>
                    <SelectTrigger data-testid="project-form-target-app"><SelectValue /></SelectTrigger>
                    <SelectContent>{TARGET_APPS.map(a => <SelectItem key={a.value} value={a.value}>{a.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label>Sort Order</Label>
                <Input data-testid="project-form-sort-order" type="number" placeholder="Auto" value={form.sort_order} onChange={e => setForm({...form, sort_order: e.target.value})} />
              </div>
              <div className="border rounded-lg p-4 space-y-3 bg-slate-50">
                <Label className="text-sm font-semibold">Media Source</Label>
                <div>
                  <Label className="text-xs text-slate-500">Media URL</Label>
                  <Input data-testid="project-form-media-url" placeholder="https://..." value={form.media_url} onChange={e => setForm({...form, media_url: e.target.value})} />
                </div>
                <div className="text-center text-xs text-slate-400">or upload a file</div>
                <div>
                  <Label className="text-xs text-slate-500">Upload Media File</Label>
                  <Input data-testid="project-form-media-file" type="file" accept="image/*,video/*,audio/*" onChange={e => setMediaFile(e.target.files[0])} />
                </div>
              </div>
              <div className="border rounded-lg p-4 space-y-3 bg-slate-50">
                <Label className="text-sm font-semibold">Thumbnail</Label>
                <div>
                  <Label className="text-xs text-slate-500">Thumbnail URL</Label>
                  <Input data-testid="project-form-thumb-url" placeholder="https://..." value={form.thumbnail_url} onChange={e => setForm({...form, thumbnail_url: e.target.value})} />
                </div>
                <div className="text-center text-xs text-slate-400">or upload an image</div>
                <div>
                  <Label className="text-xs text-slate-500">Upload Thumbnail</Label>
                  <Input data-testid="project-form-thumb-file" type="file" accept="image/*" onChange={e => setThumbFile(e.target.files[0])} />
                </div>
              </div>
              <div>
                <Label>Project Data (JSON, optional)</Label>
                <Textarea data-testid="project-form-data" placeholder='{"template": "product_ad"}' value={form.project_data} onChange={e => setForm({...form, project_data: e.target.value})} rows={3} className="font-mono text-xs" />
              </div>
              <div className="flex items-center gap-3">
                <Switch data-testid="project-form-active" checked={form.is_active} onCheckedChange={v => setForm({...form, is_active: v})} />
                <Label>Active (visible to users)</Label>
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowDialog(false)}>Cancel</Button>
              <Button data-testid="project-form-submit" type="submit" disabled={saving} className="bg-blue-600 hover:bg-blue-700">{saving ? 'Saving...' : editing ? 'Update' : 'Create'}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
